<script setup lang="ts">
/**
 * Top-right toast stack. Mount ONCE at the App root; the
 * `useToast()` composable owns the list state, so this is purely
 * the renderer.
 *
 * Visual contract:
 *   - Fixed top-right, doesn't shift layout.
 *   - Stacks vertically; newest on top (visually) so users see the
 *     latest event first.
 *   - Each toast has a coloured left rail by severity + a dismiss
 *     button.
 *   - `aria-live="polite"` so screen readers announce new toasts
 *     without interrupting the user mid-sentence.
 */
import { useToast } from "../composables/useToast";

const { toasts, dismiss } = useToast();
</script>

<template>
  <div class="toast-shelf" aria-live="polite" aria-atomic="false">
    <transition-group name="toast">
      <div
        v-for="t in toasts"
        :key="t.id"
        :class="['toast', `toast--${t.severity}`]"
        role="status"
      >
        <div class="toast__body">
          <p class="toast__msg">{{ t.message }}</p>
          <p v-if="t.detail" class="toast__detail">{{ t.detail }}</p>
        </div>
        <button
          class="toast__dismiss"
          aria-label="Dismiss notification"
          @click="dismiss(t.id)"
        >×</button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toast-shelf {
  position: fixed;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: min(420px, calc(100vw - 2 * var(--space-3)));
  pointer-events: none;
}
.toast {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem 0.6rem 0.85rem;
  border: 1px solid var(--color-border);
  border-left-width: 4px;
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: 0 4px 12px rgba(15, 29, 46, 0.08);
  font-size: 0.92rem;
  line-height: 1.4;
  color: var(--color-ink);
  pointer-events: auto;
}
.toast--error   { border-left-color: var(--color-error); }
.toast--success { border-left-color: var(--color-success); }
.toast--warning { border-left-color: #d98e00; }
.toast--info    { border-left-color: var(--color-accent); }

.toast__body { flex: 1; min-width: 0; }
.toast__msg {
  margin: 0;
  font-weight: 600;
  word-break: break-word;
}
.toast__detail {
  margin: 0.2rem 0 0;
  color: var(--color-muted);
  font-size: 0.85rem;
  font-family: var(--font-mono, ui-monospace, monospace);
  word-break: break-word;
}
.toast__dismiss {
  background: none;
  border: none;
  color: var(--color-muted);
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0.1rem 0.3rem;
  border-radius: var(--radius-sm);
  align-self: flex-start;
}
.toast__dismiss:hover { color: var(--color-ink); background: var(--color-surface-alt); }
.toast__dismiss:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}

/* Enter/leave transitions — slide in from the right, fade out. */
.toast-enter-from { opacity: 0; transform: translateX(20px); }
.toast-enter-active,
.toast-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.toast-leave-to { opacity: 0; transform: translateX(20px); }
</style>
